import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Collapse } from "@mui/material";

import { useConsumerDealsData } from "../../../Pages/Deals";

import ModalSkeleton from "../../../Components/Skeleton/modal-skeleton";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderWelcomeToStakeSaver from "./welcome-to-stake-saver";

import useCollapseComponents from "../../../CustomHooks/useCollapseComponents";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useDeals } from "../../../CustomHooks/Queries";
import { useJoinStakeSaver } from "../../../CustomHooks/Mutations";

import { dispatchAPIErrorToast } from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";

import { Trophy, Ellipse } from "../../../Assets/Images";

import "./join-stake-saver.scss";

const joinStakeSaverModalClassName =
  "join-stake-saver showcase-feature full-modal-width no-full-screen position-close-button-over-content";

export default function renderJoinStakeSaver(modalContext, { onClose } = {}) {
  const { updateModalComponent, updateModalOnCloseCallBack, setModalVariant, setModalClassName } = modalContext;

  updateModalComponent(JoinStakeSaver);
  setModalVariant(modalVariants.flexDialog);
  setModalClassName(joinStakeSaverModalClassName);
  !!onClose && updateModalOnCloseCallBack(onClose);
}

const JoinStakeSaver = () => {
  const navigate = useNavigate();
  const modalContext = useModalContext();

  const { setModalClassName, onModalClose } = useModalContext();

  const { data: deals } = useDeals();

  const { isLoadingConsumerDealsData, stakeSaverOffer } = useConsumerDealsData();

  const { showListItem, CollapseButton } = useCollapseComponents(false);

  useEffect(() => {
    if (!stakeSaverOffer) {
      onModalClose();
    }
    //eslint-disable-next-line
  }, [stakeSaverOffer]);

  useEffect(() => {
    if (showListItem) {
      setModalClassName(joinStakeSaverModalClassName.replace(" no-full-screen", ""));
    } else if (isLoadingConsumerDealsData) {
      setModalClassName(joinStakeSaverModalClassName.replace(" position-close-button-over-content", ""));
    } else {
      setModalClassName(joinStakeSaverModalClassName);
    }
    //eslint-disable-next-line
  }, [isLoadingConsumerDealsData, showListItem]);

  const { isLoading: isJoiningStakeSaver, mutate: JoinStakeSaver } = useJoinStakeSaver({
    onSuccess: () => {
      navigate("/deals/setup-stake-saver");
      renderWelcomeToStakeSaver(modalContext, stakeSaverOffer);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "joinStakeSaver" }),
  });

  const stakeSaverFrequencies = useMemo(() => {
    return stakeSaverOffer?.check_out_details?.frequency ?? [];
  }, [stakeSaverOffer]);

  const handleJoinStakeSaver = () => {
    if (stakeSaverFrequencies.length > 0) {
      navigate("/deals/stake-saver");
      onModalClose({ abortCallingOnCloseCallback: true });
    } else {
      JoinStakeSaver({
        consumer_sfid: deals?.data?.data?.currentDeal?.sfid,
      });
    }
  };

  return isLoadingConsumerDealsData ? (
    <ModalSkeleton />
  ) : (
    <>
      <div className="modal-image with-ellipse">
        <img className="trophy" src={Trophy} alt="" />
        <Ellipse className="ellipse" />
        <div className="modal-content-ellipse"></div>
      </div>
      <div className="modal-dialog-content">
        <h3 className="text-2xl-medium color--primary-400 text-center mb-4">
          {stakeSaverOffer?.offer_details?.title}
          <br></br>
          <span className="color--mist">{stakeSaverOffer?.offer_details?.sub_title}</span>
        </h3>
        <span className="stake-saver-description">{stakeSaverOffer?.offer_details?.description}</span>
        <div className="mt-8 mb-6">
          <PrimaryButton onClick={handleJoinStakeSaver} disabled={isJoiningStakeSaver}>
            {stakeSaverOffer?.offer_details?.action_button_label} {isJoiningStakeSaver && <CircularProgress />}
          </PrimaryButton>
          <CollapseButton className="learn-more-button">Learn More</CollapseButton>
        </div>
        <Collapse in={showListItem}>
          <div className="text-lg-medium color--mist mb-4">
            {stakeSaverOffer?.offer_details?.learn_more_description}
          </div>
          <div className="numbered-list mb-4">
            {stakeSaverOffer?.offer_details?.learn_more_items?.map((benefit, idx) => (
              <div key={idx} className="list-item">
                <span className="list-item-badge">✅</span>
                <div className="list-item-content">{benefit}</div>
              </div>
            ))}
          </div>
          <div className="text-base-medium color--primary-400 mb-4">
            {stakeSaverOffer?.offer_details?.learn_more_footer}
          </div>
        </Collapse>
      </div>
    </>
  );
};
